import {
  keepPreviousData,
  type QueryClient,
  useMutation,
  useQuery,
} from "@tanstack/react-query";

import useApi from "../../api/apiConfig";
import useDevMode from "../../utils/devmode";
import {
  updateClaim,
  updateDenial,
  updateStringifyClaim,
} from "../ClaimsDashboard/api";
import {
  createAIEntity,
  createPayment,
  getPayments,
  getPaymentWithClaims,
  type PaymentDetail,
  type PaymentsSearchParams,
  updatePayment,
} from "./api";

const PAYMENTS_KEY = ["payments"];
const LIST_KEY = [...PAYMENTS_KEY, "list"];
const DETAIL_KEY = [...PAYMENTS_KEY, "detail"];

export const paymentDetailKey = (paymentId: string) => [
  ...DETAIL_KEY,
  paymentId,
];

export const usePayments = (params: PaymentsSearchParams, enabled: boolean) => {
  const { getClient } = useApi();
  return useQuery({
    queryKey: [...LIST_KEY, params],
    queryFn: async ({ signal }) =>
      getPayments(await getClient(), params, signal),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 0,
    retry: false,
  });
};

export const usePaymentDetail = (paymentId: string | undefined) => {
  const { getClient } = useApi();
  const devMode = useDevMode();
  return useQuery({
    queryKey: paymentDetailKey(paymentId ?? ""),
    queryFn: async ({ signal }) =>
      getPaymentWithClaims(await getClient(), paymentId ?? "", devMode, signal),
    enabled: Boolean(paymentId),
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });
};

export const useCreatePayment = () => {
  const { getClient } = useApi();
  return useMutation({
    mutationFn: async (payment: PaymentMessage) =>
      createPayment(await getClient(), payment),
  });
};

export const useUpdatePayment = () => {
  const { getClient } = useApi();
  return useMutation({
    mutationFn: async (payment: Partial<PaymentMessage>) =>
      updatePayment(await getClient(), payment),
  });
};

export const useCreateAIEntity = () => {
  const { getClient } = useApi();
  return useMutation({
    mutationFn: async ({
      aiMessage,
      fileId,
    }: {
      aiMessage: AIMessage;
      fileId: string;
    }) => createAIEntity(await getClient(), aiMessage, fileId),
  });
};

export const useUpdateClaim = () => {
  const { getClient } = useApi();
  return useMutation({
    mutationFn: async (claimWork: ClaimWork) =>
      updateClaim(await getClient(), claimWork),
  });
};

export const useUpdateStringifyClaim = () => {
  const { getClient } = useApi();
  return useMutation({
    mutationFn: async (claim: Partial<ClaimMessage>) =>
      updateStringifyClaim(await getClient(), claim),
  });
};

export const useUpdateDenial = () => {
  const { getClient } = useApi();
  return useMutation({
    mutationFn: async ({
      claimWork,
      denialWork,
    }: {
      claimWork: ClaimWork;
      denialWork?: DenialWork;
    }) => updateDenial(await getClient(), claimWork, denialWork),
  });
};

export function patchPaymentRow(
  queryClient: QueryClient,
  payment: PaymentMessage,
) {
  queryClient.setQueriesData<SearchResponse>({ queryKey: LIST_KEY }, (old) => {
    if (!old?.data.some((p) => p.wieldyId === payment.wieldyId)) return old;
    return {
      ...old,
      data: old.data.map((p) =>
        p.wieldyId === payment.wieldyId ? payment : p,
      ),
    };
  });
}

// Appends to the query on screen only. A prefix match would also write the row
// into cached lists for other filters and pages.
export function appendPaymentRow(
  queryClient: QueryClient,
  params: PaymentsSearchParams,
  payment: PaymentMessage,
) {
  queryClient.setQueryData<SearchResponse>([...LIST_KEY, params], (old) =>
    old ? { ...old, data: [...old.data, payment] } : old,
  );
}

function updateDetailClaims(
  queryClient: QueryClient,
  paymentId: string | null,
  update: (
    claims: ClaimWithProcedureAndPatientMessage[],
  ) => ClaimWithProcedureAndPatientMessage[],
  claimKey?: string,
) {
  queryClient.setQueriesData<PaymentDetail>(
    { queryKey: paymentId ? paymentDetailKey(paymentId) : DETAIL_KEY },
    (old) => {
      if (!old) return old;
      if (claimKey && !old.claims.some((c) => c.wieldyId === claimKey)) {
        return old;
      }
      return { ...old, claims: update(old.claims) };
    },
  );
}

function updateClaimRow(
  queryClient: QueryClient,
  claimKey: string,
  update: (
    claim: ClaimWithProcedureAndPatientMessage,
  ) => ClaimWithProcedureAndPatientMessage,
) {
  updateDetailClaims(
    queryClient,
    null,
    (claims) => claims.map((c) => (c.wieldyId === claimKey ? update(c) : c)),
    claimKey,
  );
}

export function appendDetailClaim(
  queryClient: QueryClient,
  paymentId: string,
  claim: ClaimWithProcedureAndPatientMessage,
) {
  updateDetailClaims(queryClient, paymentId, (claims) => [...claims, claim]);
}

export type PaymentsCacheAction =
  | { type: "UPDATE_CLAIM"; claimToUpdate: ClaimWithProcedureAndPatientMessage }
  | { type: "UPDATE_PAYMENT"; paymentToUpdate: PaymentMessage }
  | { type: "DELETE_CLAIM"; claimKey: string }
  | {
      type: "CREATE_PROCEDURE" | "UPDATE_PROCEDURE";
      claimKey: string;
      procedure: ProcedureMessage;
    }
  | { type: "DELETE_PROCEDURE"; claimKey: string; procedureKey: string };

// Lets the shared crud/posting/validation helpers keep their dispatch-style callback.
export function applyPaymentsAction(
  queryClient: QueryClient,
  action: PaymentsCacheAction,
) {
  switch (action.type) {
    case "UPDATE_CLAIM":
      updateClaimRow(
        queryClient,
        action.claimToUpdate.wieldyId,
        () => action.claimToUpdate,
      );
      break;
    case "UPDATE_PAYMENT":
      patchPaymentRow(queryClient, action.paymentToUpdate);
      break;
    case "DELETE_CLAIM":
      updateDetailClaims(
        queryClient,
        null,
        (claims) => claims.filter((c) => c.wieldyId !== action.claimKey),
        action.claimKey,
      );
      break;
    case "UPDATE_PROCEDURE":
      updateClaimRow(queryClient, action.claimKey, (claim) => ({
        ...claim,
        Procedure: claim.Procedure.map((proc) =>
          proc.wieldyId === action.procedure.wieldyId ? action.procedure : proc,
        ),
      }));
      break;
    case "CREATE_PROCEDURE":
      updateClaimRow(queryClient, action.claimKey, (claim) => ({
        ...claim,
        Procedure: [...claim.Procedure, action.procedure],
      }));
      break;
    case "DELETE_PROCEDURE":
      updateClaimRow(queryClient, action.claimKey, (claim) => ({
        ...claim,
        Procedure: claim.Procedure.filter(
          (procedure) => procedure.wieldyId !== action.procedureKey,
        ),
      }));
      break;
    default:
      break;
  }
}
